import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Settings,
  BarChart3,
  PieChart,
  AlertCircle,
  RefreshCw,
  Search,
  Menu,
  X,
  Scan,
  Github,
  LogOut,
  User,
  Plus,
  Save,
  Check
} from 'lucide-react'
import Dashboard from './components/Dashboard'
import Signals from './components/Signals'
import StockDetail from './components/StockDetail'
import SettingsPage from './components/Settings'
import MarketScan from './components/MarketScan'
import ETFVolume from './components/ETFVolume'
import Login from './components/Login'
import { isAuthenticated, clearAuth, verifyToken, setOnUnauthorized, getConfig, updateStockPool, getMarketContext } from './api/client'
import './index.css'

function App() {
  const [loggedIn, setLoggedIn] = useState<boolean>(false)
  const [authChecked, setAuthChecked] = useState<boolean>(false)
  const [currentUsername, setCurrentUsername] = useState<string>('')
  const [activeTab, setActiveTab] = useState('dashboard')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [selectedStock, setSelectedStock] = useState<string | null>(null)
  const [marketData, setMarketData] = useState({
    indexPhase: 'PHASE_2_RISING',
    sentiment: 'bullish',
    lastUpdate: new Date().toLocaleString('zh-CN')
  })

  // 股票池状态
  const [stockPoolMap, setStockPoolMap] = useState<Record<string, string>>({})
  const [poolCodeInput, setPoolCodeInput] = useState('')
  const [poolNameInput, setPoolNameInput] = useState('')
  const [poolSaving, setPoolSaving] = useState(false)
  const [poolMsg, setPoolMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  const handleLogout = useCallback(() => {
    clearAuth()
    setLoggedIn(false)
    setCurrentUsername('')
  }, [])

  // 设置全局 401 回调
  useEffect(() => {
    setOnUnauthorized(handleLogout)
  }, [handleLogout])

  // 检查现有 token 是否有效
  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        const valid = await verifyToken()
        if (valid) {
          setLoggedIn(true)
          setCurrentUsername(localStorage.getItem('auth_username') || '')
        } else {
          clearAuth()
        }
      }
      setAuthChecked(true)
    }
    checkAuth()
  }, [])

  const handleLoginSuccess = (token: string, username: string) => {
    setLoggedIn(true)
    setCurrentUsername(username)
  }

  // 加载股票池配置
  useEffect(() => {
    if (!loggedIn) return
    getConfig()
      .then((cfg) => setStockPoolMap(cfg.stock_pool_map || {}))
      .catch(() => {})
  }, [loggedIn])

  // 加载市场环境数据
  useEffect(() => {
    if (!loggedIn) return
    getMarketContext()
      .then((ctx) => {
        setMarketData({
          indexPhase: ctx.index_phase || 'UNKNOWN',
          sentiment: ctx.market_sentiment || '',
          lastUpdate: new Date().toLocaleString('zh-CN')
        })
      })
      .catch(() => {})
  }, [loggedIn])

  const handleAddStock = () => {
    const code = poolCodeInput.trim()
    const name = poolNameInput.trim()
    if (!/^\d{6}$/.test(code)) { setPoolMsg({ type: 'err', text: '请输入6位股票代码' }); return }
    if (!name) { setPoolMsg({ type: 'err', text: '请输入股票名称' }); return }
    if (stockPoolMap[code]) { setPoolMsg({ type: 'err', text: '已存在' }); return }
    setStockPoolMap({ ...stockPoolMap, [code]: name })
    setPoolCodeInput('')
    setPoolNameInput('')
    setPoolMsg(null)
  }

  const handleRemoveStock = (code: string) => {
    const next = { ...stockPoolMap }
    delete next[code]
    setStockPoolMap(next)
  }

  const handleSavePool = async () => {
    let nextMap = { ...stockPoolMap }
    const pendingCode = poolCodeInput.trim()
    const pendingName = poolNameInput.trim()
    if (pendingCode || pendingName) {
      if (!/^\d{6}$/.test(pendingCode)) { setPoolMsg({ type: 'err', text: '请输入6位股票代码' }); return }
      if (!pendingName) { setPoolMsg({ type: 'err', text: '请输入股票名称' }); return }
      nextMap[pendingCode] = pendingName
      setStockPoolMap(nextMap)
      setPoolCodeInput('')
      setPoolNameInput('')
    }
    if (Object.keys(nextMap).length === 0) { setPoolMsg({ type: 'err', text: '至少保留一个股票' }); return }
    try {
      setPoolSaving(true)
      await updateStockPool(nextMap)
      setPoolMsg({ type: 'ok', text: '已保存' })
      setTimeout(() => setPoolMsg(null), 2000)
    } catch (e: any) {
      setPoolMsg({ type: 'err', text: e.message || '保存失败' })
    } finally {
      setPoolSaving(false)
    }
  }

  // 等待认证检查完成
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-cyan-400 rounded-xl flex items-center justify-center animate-pulse">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <p className="text-gray-400 text-sm">验证登录状态...</p>
        </div>
      </div>
    )
  }

  // 未登录，显示登录页面
  if (!loggedIn) {
    return <Login onLoginSuccess={handleLoginSuccess} />
  }

  const navItems = [
    { id: 'dashboard', label: '监控面板', icon: BarChart3 },
    { id: 'marketscan', label: '股票池扫描', icon: Scan },
    { id: 'signals', label: '交易信号', icon: Activity },
    { id: 'etfdata', label: 'ETF数据', icon: PieChart },
    { id: 'settings', label: '系统设置', icon: Settings },
  ]

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onStockSelect={setSelectedStock} marketData={marketData} />
      case 'marketscan':
        return <MarketScan />
      case 'signals':
        return <Signals onStockSelect={setSelectedStock} />
      case 'etfdata':
        return <ETFVolume />
      case 'settings':
        return <SettingsPage />
      default:
        return <Dashboard onStockSelect={setSelectedStock} marketData={marketData} />
    }
  }

  return (
    <div className="min-h-screen bg-dark flex">
      {/* Sidebar */}
      <motion.aside
        initial={{ x: -280 }}
        animate={{ x: sidebarOpen ? 0 : -280 }}
        transition={{ duration: 0.3 }}
        className="fixed left-0 top-0 h-full w-70 bg-dark-lighter border-r border-white/10 z-50"
      >
        <div className="p-6 flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-cyan-400 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">30周均线系统</h1>
              <a
                href="https://www.bilibili.com/video/BV1twFSzNE75"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                title="点击观看理论讲解视频"
              >
                基于史丹-温斯坦
              </a>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                    activeTab === item.id
                      ? 'bg-primary/20 text-primary border border-primary/30'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              )
            })}
          </nav>

          {/* Market Status */}
          <div className="mt-10 p-4 glass-card">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">市场环境</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">大盘阶段</span>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  marketData.indexPhase === 'PHASE_2_RISING'
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : marketData.indexPhase === 'PHASE_4_FALLING'
                    ? 'bg-red-500/20 text-red-400'
                    : marketData.indexPhase === 'PHASE_1_BOTTOM'
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {marketData.indexPhase === 'PHASE_2_RISING' ? '上升趋势'
                    : marketData.indexPhase === 'PHASE_4_FALLING' ? '下降趋势'
                    : marketData.indexPhase === 'PHASE_1_BOTTOM' ? '底部筑底'
                    : marketData.indexPhase === 'PHASE_3_TOP' ? '顶部横盘'
                    : '未知'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">市场情绪</span>
                <span className={`text-xs font-medium ${
                  marketData.sentiment.includes('bullish') || marketData.sentiment.includes('看涨') ? 'text-emerald-400'
                    : marketData.sentiment.includes('bearish') || marketData.sentiment.includes('看跌') ? 'text-red-400'
                    : 'text-yellow-400'
                }`}>
                  {marketData.sentiment.includes('bullish') || marketData.sentiment.includes('看涨') ? '看涨'
                    : marketData.sentiment.includes('bearish') || marketData.sentiment.includes('看跌') ? '看跌'
                    : marketData.sentiment || '未知'}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              更新: {marketData.lastUpdate}
            </p>
          </div>

          {/* Stock Pool */}
          <div className="mt-4 p-4 glass-card">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">股票池</h3>
            <div className="flex flex-wrap gap-1.5 mb-3 max-h-32 overflow-y-auto">
              {Object.entries(stockPoolMap).map(([code, name]) => (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/10 text-gray-300 rounded text-xs group"
                >
                  <span className="font-medium">{name}</span>
                  <span className="font-mono text-gray-500">{code}</span>
                  <button
                    onClick={() => handleRemoveStock(code)}
                    className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {Object.keys(stockPoolMap).length === 0 && (
                <span className="text-xs text-gray-500">暂无</span>
              )}
            </div>
            <div className="grid grid-cols-[0.8fr_1fr_auto] gap-1.5">
              <input
                type="text"
                value={poolCodeInput}
                onChange={(e) => setPoolCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddStock()}
                placeholder="代码"
                maxLength={6}
                className="min-w-0 px-2 py-1 bg-dark border border-white/10 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 font-mono"
              />
              <input
                type="text"
                value={poolNameInput}
                onChange={(e) => setPoolNameInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddStock()}
                placeholder="名称"
                className="min-w-0 px-2 py-1 bg-dark border border-white/10 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:border-primary/50"
              />
              <button
                onClick={handleAddStock}
                className="p-1 text-gray-400 hover:text-primary transition-colors"
                title="添加"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleSavePool}
              disabled={poolSaving}
              className="w-full mt-2 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-primary/20 text-primary rounded text-xs hover:bg-primary/30 transition-colors disabled:opacity-50 font-medium"
            >
              {poolSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : poolMsg?.type === 'ok' ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
              {poolSaving ? '保存中...' : poolMsg?.type === 'ok' ? '已保存' : '保存'}
            </button>
            {poolMsg?.type === 'err' && (
              <p className="text-xs text-red-400 mt-1">{poolMsg.text}</p>
            )}
          </div>

          {/* User Info & Logout at bottom */}
          <div className="mt-auto pt-6 border-t border-white/10">
            <div className="flex items-center gap-3 px-2 mb-3">
              <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{currentUsername}</p>
                <p className="text-xs text-gray-500">已登录</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-300"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">退出登录</span>
            </button>
          </div>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-70' : 'ml-0'}`}>
        {/* Header */}
        <header className="sticky top-0 z-40 glass-strong border-b border-white/10">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索股票代码或名称..."
                  className="w-64 pl-10 pr-4 py-2 bg-dark-lighter border border-white/10 rounded-lg text-sm focus:outline-none focus:border-primary/50 transition-colors"
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <a
                href="https://github.com/oficcejo/stock-ma30.git"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 hover:text-white transition-colors"
              >
                <Github className="w-4 h-4" />
                <span className="text-sm font-medium">GitHub</span>
              </a>
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="text-sm font-medium">刷新页面</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                title="退出登录"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </main>

      {/* Stock Detail Modal */}
      {selectedStock && (
        <StockDetail
          stockCode={selectedStock}
          onClose={() => setSelectedStock(null)}
        />
      )}
    </div>
  )
}

export default App
